import React, { useEffect, useRef } from 'react';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat } from 'ol/proj';
import { Icon, Style } from 'ol/style';
import Overlay from 'ol/Overlay';

const MapWithOpenLayers = ({ founders = [], onMarkerClick }) => {
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current && containerRef.current) {
      // Base map
      const base = new TileLayer({ source: new OSM() });

      // Vector layer for markers
      const vectorSource = new VectorSource();
      const vectorLayer = new VectorLayer({ source: vectorSource });

      const map = new Map({
        target: containerRef.current,
        layers: [base, vectorLayer],
        view: new View({
          center: fromLonLat([35.0, -6.5]), // Tanzania approx center
          zoom: 5.5,
        }),
      });

      // Popup overlay
      const overlay = new Overlay({
        element: popupRef.current,
        autoPan: true,
        autoPanAnimation: { duration: 250 },
      });
      map.addOverlay(overlay);

      mapRef.current = { map, vectorSource, overlay };
    }

    // Add founder markers
    if (mapRef.current) {
      const { vectorSource } = mapRef.current;
      vectorSource.clear();

      founders.forEach((f) => {
        if (typeof f.lng !== 'number' || typeof f.lat !== 'number') return;
        const feature = new Feature({
          geometry: new Point(fromLonLat([f.lng, f.lat])),
          data: f,
        });
        feature.setStyle(
          new Style({
            image: new Icon({
              anchor: [0.5, 1],
              src: 'data:image/svg+xml;utf8,' + encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24">
                  <path fill="%23FF6347" d="M12 2c3.866 0 7 3.028 7 6.768C19 13.43 12 22 12 22S5 13.43 5 8.768C5 5.028 8.134 2 12 2m0 9.25a2.75 2.75 0 1 0 0-5.5a2.75 2.75 0 0 0 0 5.5"/>
                </svg>`),
              scale: 1,
            }),
          })
        );
        vectorSource.addFeature(feature);
      });

      // Click interaction for popup and navigation
      mapRef.current.map.on('singleclick', (evt) => {
        const features = mapRef.current.map.getFeaturesAtPixel(evt.pixel);
        if (features && features.length > 0) {
          const f = features[0];
          const data = f.get('data');
          if (data) {
            const coordinate = evt.coordinate;
            mapRef.current.overlay.setPosition(coordinate);
            if (popupRef.current) {
              popupRef.current.innerHTML = `
                <div style="min-width:180px; max-width:220px; font-family: system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, 'Helvetica Neue', Arial, 'Apple Color Emoji', 'Segoe UI Emoji';">
                  <div style="font-weight:700; margin-bottom:4px; color:#111827; cursor: pointer;" data-founder-id="${data.id}">${data.name || data.company || 'Space'}</div>
                  <div style="font-size:12px; color:#6b7280;">${(data.extra && (data.extra.sectorFocus || data.company)) || ''}</div>
                  <div style="font-size:10px; color:#FF6347; margin-top:4px; font-weight:600;">Click name to view details</div>
                </div>`;
              
              // Add click listener to the popup name
              const nameElement = popupRef.current.querySelector('[data-founder-id]');
              if (nameElement && onMarkerClick) {
                nameElement.addEventListener('click', () => {
                  console.log('Popup name clicked, navigating to founder ID:', data.id);
                  onMarkerClick(data.id);
                });
              }
            }
            
            // Also call navigation when marker is clicked (not just popup)
            if (onMarkerClick && data.id) {
              console.log('Map marker clicked, navigating to founder ID:', data.id);
              onMarkerClick(data.id);
            }
          }
        }
      });
    }
  }, [founders, onMarkerClick]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%', borderRadius: 8, overflow: 'hidden' }} />
      <div ref={popupRef} style={{ position: 'absolute', background: '#fff', borderRadius: 8, padding: '8px 10px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)', transform: 'translate(-50%, -110%)' }} />
    </div>
  );
};

export default MapWithOpenLayers;
